import React from "react";
import { Badge } from "@/components/ui/badge";
import { CheckSquare, Square } from "lucide-react";
import RevenueChart from "@/components/report/RevenueChart";
import Glossary from "@/components/Glossary";

function Section({ title, children, term }) {
  return (
    <section>
      <h2 className="text-2xl sm:text-3xl font-medium tracking-tight text-slate-900 mb-4 inline-flex items-center gap-1.5">
        <span>{title}</span>
        {term && <Glossary term={term} />}
      </h2>
      <div className="rounded-2xl border border-gray-200 bg-slate-50/40 p-6 sm:p-8">
        {children}
      </div>
    </section>
  );
}

function Prose({ text }) {
  if (!text) return null;
  return <p className="text-slate-700 leading-relaxed whitespace-pre-line">{text}</p>;
}

function SwotBlock({ swot }) {
  if (!swot) return null;
  const groups = [
    { key: "strengths", label: "Styrker", color: "bg-emerald-50 border-emerald-100 text-emerald-900" },
    { key: "weaknesses", label: "Svakheter", color: "bg-rose-50 border-rose-100 text-rose-900" },
    { key: "opportunities", label: "Muligheter", color: "bg-amber-50 border-amber-100 text-amber-900" },
    { key: "threats", label: "Trusler", color: "bg-slate-50 border-slate-200 text-slate-900" },
  ];
  return (
    <div className="grid sm:grid-cols-2 gap-4">
      {groups.map((g) => (
        <div key={g.key} className={`rounded-2xl border p-6 ${g.color}`}>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] mb-3 opacity-70">{g.label}</p>
          <ul className="space-y-2 text-sm leading-relaxed">
            {(swot[g.key] || []).map((s, i) => (
              <li key={i} className="flex items-start gap-2">
                <span className="mt-2 h-1 w-1 rounded-full bg-current shrink-0 opacity-60" />
                <span>{s}</span>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

function ActionPlan({ steps }) {
  if (!Array.isArray(steps)) return null;
  return (
    <div className="space-y-4">
      {steps.map((s, i) => (
        <div key={i} className="rounded-2xl border border-gray-200 p-6 bg-white">
          <div className="flex items-center gap-3">
            <div className="h-8 w-8 rounded-full bg-slate-900 text-white flex items-center justify-center text-sm font-medium">{i + 1}</div>
            <p className="text-sm font-medium text-slate-900">{s.phase}</p>
            {s.timeline && <span className="text-xs text-slate-500">· {s.timeline}</span>}
          </div>
          <p className="mt-3 text-slate-700 leading-relaxed">{s.actions}</p>
        </div>
      ))}
    </div>
  );
}

function BudgetBlock({ budget }) {
  if (!budget) return null;
  const fields = [
    { key: "monthly_revenue", label: "Mnd. omsetning" },
    { key: "monthly_costs", label: "Mnd. kostnader" },
    { key: "breakeven", label: "Breakeven" },
  ];
  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-8">
      <div className="grid sm:grid-cols-3 gap-6">
        {fields.map((f) => (
          <div key={f.key}>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">{f.label}</p>
            <p className="mt-2 text-2xl font-medium tracking-tight text-slate-900">{budget[f.key]}</p>
          </div>
        ))}
      </div>
      {Array.isArray(budget.revenue_projection) && budget.revenue_projection.length > 0 && (
        <>
          <div className="my-6 h-px bg-gray-100" />
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 mb-3">Omsetning per måned</p>
          <RevenueChart data={budget.revenue_projection} />
        </>
      )}
      {budget.assumptions && (
        <>
          <div className="my-6 h-px bg-gray-100" />
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 mb-2">Forutsetninger</p>
          <p className="text-slate-700 leading-relaxed">{budget.assumptions}</p>
        </>
      )}
    </div>
  );
}

function MarketSize({ market }) {
  if (!market) return null;
  const items = [
    { key: "tam", label: "TAM", desc: "Totalt marked" },
    { key: "sam", label: "SAM", desc: "Serviceable marked" },
    { key: "som", label: "SOM", desc: "Realistisk andel" },
  ];
  return (
    <div className="grid sm:grid-cols-3 gap-4">
      {items.map((i) => (
        <div key={i.key} className="rounded-2xl border border-gray-200 bg-white p-6">
          <div className="flex items-baseline gap-2">
            <span className="text-2xl font-medium tracking-tight text-slate-900">{i.label}</span>
            <span className="text-xs text-slate-500">{i.desc}</span>
          </div>
          <p className="mt-3 text-sm text-slate-700 leading-relaxed">{market[i.key] || "—"}</p>
        </div>
      ))}
    </div>
  );
}

function KPIs({ kpis }) {
  if (!Array.isArray(kpis)) return null;
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {kpis.map((k, i) => (
        <div key={i} className="rounded-2xl border border-gray-200 bg-white p-6">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">{k.name}</p>
          <p className="mt-2 text-2xl font-medium tracking-tight text-slate-900">{k.target}</p>
          {k.unit && <p className="mt-1 text-xs text-slate-500">{k.unit}</p>}
        </div>
      ))}
    </div>
  );
}

function Risks({ risks }) {
  if (!Array.isArray(risks)) return null;
  return (
    <div className="space-y-3">
      {risks.map((r, i) => (
        <div key={i} className="rounded-2xl border border-gray-200 bg-white p-6">
          <div className="flex items-start gap-3">
            <div className="h-8 w-8 rounded-full bg-rose-100 text-rose-700 flex items-center justify-center text-sm font-medium shrink-0">{i + 1}</div>
            <div className="flex-1">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-rose-700">Risiko</p>
              <p className="mt-1 text-slate-900 font-medium">{r.risk}</p>
              <div className="mt-3 pt-3 border-t border-gray-100">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-700">Tiltak</p>
                <p className="mt-1 text-slate-700 leading-relaxed">{r.mitigation}</p>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function NextSteps({ steps }) {
  if (!Array.isArray(steps)) return null;
  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-6 sm:p-8">
      <ul className="space-y-3">
        {steps.map((s, i) => (
          <li key={i} className="flex items-start gap-3 text-slate-800">
            <Square className="h-5 w-5 text-slate-400 shrink-0 mt-0.5" />
            <span className="leading-relaxed">{s}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Conclusion({ text }) {
  if (!text) return null;
  return (
    <div className="rounded-2xl bg-slate-900 text-white p-8">
      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-300 mb-4">Konklusjon</p>
      <p className="text-lg leading-relaxed text-slate-100 whitespace-pre-line">{text}</p>
    </div>
  );
}

export default function PlanContent({ plan }) {
  const { inputs = {}, sections = {} } = plan || {};
  const title = inputs.business_name || plan?.title || "Forretningsplan";
  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-10 sm:p-14 printable">
      {/* Cover */}
      <div className="border-b border-gray-100 pb-10 print-cover">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Forretningsplan</p>
        <h1 className="mt-3 text-4xl sm:text-5xl font-medium tracking-tight text-slate-900">{title}</h1>
        {inputs.business_idea && <p className="mt-4 text-lg text-slate-600 leading-relaxed">{inputs.business_idea}</p>}
        <div className="mt-6 flex flex-wrap gap-2 text-sm">
          {inputs.industry && <Badge variant="secondary" className="rounded-full">{inputs.industry}</Badge>}
          {inputs.company_form && <Badge variant="secondary" className="rounded-full">{inputs.company_form}</Badge>}
          {inputs.target_market && <Badge variant="secondary" className="rounded-full">{inputs.target_market}</Badge>}
        </div>
      </div>

      <div className="mt-12 space-y-14">
        <Section title="Sammendrag"><Prose text={sections.executive_summary} /></Section>
        <Section title="Forretningsidé"><Prose text={sections.business_idea} /></Section>
        <Section title="Målgruppe"><Prose text={sections.target_audience} /></Section>
        <Section title="Problem & løsning"><Prose text={sections.problem_solution} /></Section>
        <Section title="Inntektsmodell"><Prose text={sections.revenue_model} /></Section>
        {sections.market_size && <Section title="Markedsstørrelse" term="tam"><MarketSize market={sections.market_size} /></Section>}
        <Section title="SWOT-analyse" term="swot"><SwotBlock swot={sections.swot} /></Section>
        <Section title="Markedsføringsplan"><Prose text={sections.marketing_plan} /></Section>
        <Section title="Handlingsplan"><ActionPlan steps={sections.action_plan} /></Section>
        {sections.kpis && <Section title="Nøkkeltall (KPI-er)" term="kpi"><KPIs kpis={sections.kpis} /></Section>}
        <Section title="Budsjett" term="breakeven"><BudgetBlock budget={sections.budget} /></Section>
        {sections.risks && <Section title="Risikoanalyse"><Risks risks={sections.risks} /></Section>}
        {sections.next_steps && <Section title="Neste steg"><NextSteps steps={sections.next_steps} /></Section>}
        {sections.conclusion && <Section title="Konklusjon"><Conclusion text={sections.conclusion} /></Section>}
      </div>

      <div className="mt-16 pt-8 border-t border-gray-100 text-xs text-slate-400 text-center">
        Generert med IdeSetGo · {new Date().toLocaleDateString("nb-NO", { day: "numeric", month: "long", year: "numeric" })}
      </div>
    </div>
  );
}

// Named exports (also used by PlanView for editable variants)
export { Section, Prose, SwotBlock, ActionPlan, BudgetBlock, MarketSize, KPIs, Risks, NextSteps, Conclusion };
